/**
 * Publication / Analysis chart colors derived from main nav (Navigation.css .nav-item.*).
 * Keep in sync when nav colors change.
 */
export const NAV_THEME = {
  ucBlue: '#005581',
  ucTeal: '#00778B',
  ucLightBlue: '#72CDF4',
  ucGold: '#FFB511',
  ucPurple: '#6B46C1',
  ucOrange: '#FF6E1B',
  ucPink: '#E44C9A',
  ucGreen: '#10b981',
  ucGray: '#6b7280',
  ucSlate: '#475569',
} as const;

const HEX = /^#?([0-9a-fA-F]{6})$/;

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = hex.trim().match(HEX);
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Stacked area / line: solid border + translucent fill */
export function navStack(hex: string, fillAlpha: number): { border: string; fill: string } {
  const { r, g, b } = hexToRgb(hex);
  return {
    border: `rgb(${r}, ${g}, ${b})`,
    fill: `rgba(${r}, ${g}, ${b}, ${fillAlpha})`,
  };
}

/** Border and fill both with alpha (e.g. undetermined electricity overlay). */
export function navStackDual(hex: string, borderAlpha: number, fillAlpha: number): { border: string; fill: string } {
  const { r, g, b } = hexToRgb(hex);
  return {
    border: `rgba(${r}, ${g}, ${b}, ${borderAlpha})`,
    fill: `rgba(${r}, ${g}, ${b}, ${fillAlpha})`,
  };
}

/**
 * UC Berkeley brand-adjacent hexes (communications.berkeley.edu): Berkeley Blue, California Gold,
 * Founder's Rock, Golden Gate, Lawrence; plus harmonizing tones for distinct sectors.
 */
export const BERKELEY_BRAND = {
  berkeleyBlue: '#003262',
  californiaGold: '#FDB515',
  foundersRock: '#3B7EA1',
  goldenGate: '#ED4E33',
  lawrence: '#85929F',
  /** Darker gold / bronze — natural gas vs electric gold */
  medalistBronze: '#B78727',
  /** Extended palette teal-green */
  beanMillennial: '#006B5B',
  /** Cool neutral gray */
  neutralGray: '#63666A',
  /** Blue-violet harmonizing with primary blue */
  satherViolet: '#4A3D7A',
} as const;

/**
 * Analysis page sector GHG inventory charts — closest Berkeley matches per category.
 */
const GHG_SECTOR_HEX = {
  transportBlue: BERKELEY_BRAND.berkeleyBlue,
  naturalGasOrange: BERKELEY_BRAND.medalistBronze,
  electricityYellow: BERKELEY_BRAND.californiaGold,
  agricultureGreen: BERKELEY_BRAND.beanMillennial,
  wastePink: BERKELEY_BRAND.goldenGate,
  offroadTeal: BERKELEY_BRAND.foundersRock,
  industrialBlueGray: BERKELEY_BRAND.neutralGray,
  highGwpPurple: BERKELEY_BRAND.satherViolet,
} as const;

export const ANALYSIS_SECTOR_GHG_PALETTE = {
  transportation: navStack(GHG_SECTOR_HEX.transportBlue, 1),
  naturalGas: navStack(GHG_SECTOR_HEX.naturalGasOrange, 1),
  electricity: navStack(GHG_SECTOR_HEX.electricityYellow, 1),
  agriculture: navStack(GHG_SECTOR_HEX.agricultureGreen, 1),
  waste: navStackDual(GHG_SECTOR_HEX.wastePink, 1, 0.9),
  offroad: navStackDual(GHG_SECTOR_HEX.offroadTeal, 1, 0.9),
  industrial: navStackDual(GHG_SECTOR_HEX.industrialBlueGray, 1, 0.6),
  highGwp: navStackDual(GHG_SECTOR_HEX.highGwpPurple, 1, 0.5),
  /** Same hue as electricity; 60% transparent → 40% opaque fill. */
  undeterminedElectricity: navStackDual(GHG_SECTOR_HEX.electricityYellow, 1, 0.4),
  /** Right-hand CARB chart only: no translucent fills. */
  wasteCarb: navStack(GHG_SECTOR_HEX.wastePink, 1),
  industrialCarb: navStack(GHG_SECTOR_HEX.industrialBlueGray, 1),
  highGwpCarb: navStack(GHG_SECTOR_HEX.highGwpPurple, 1),
} as const;

/** County stacked chart (Analysis): rotate Berkeley brand + harmonizing tones. */
export const NAV_CHART_CYCLE: readonly string[] = [
  BERKELEY_BRAND.berkeleyBlue,
  BERKELEY_BRAND.californiaGold,
  BERKELEY_BRAND.foundersRock,
  BERKELEY_BRAND.goldenGate,
  BERKELEY_BRAND.medalistBronze,
  BERKELEY_BRAND.beanMillennial,
  BERKELEY_BRAND.lawrence,
  BERKELEY_BRAND.satherViolet,
  '#5c7a99',
  '#d9a514',
];

/**
 * County stacked chart: cycle Berkeley-themed colors with staggered fill alpha so repeats stay distinguishable.
 */
export function countySeriesColors(index: number): { border: string; fill: string } {
  const hex = NAV_CHART_CYCLE[index % NAV_CHART_CYCLE.length] ?? BERKELEY_BRAND.berkeleyBlue;
  const tier = Math.floor(index / NAV_CHART_CYCLE.length);
  const alpha = Math.min(0.88, 0.52 + tier * 0.1);
  const borderAlpha = Math.min(1, 0.82 + tier * 0.04);
  const { r, g, b } = hexToRgb(hex);
  return {
    border: `rgba(${r}, ${g}, ${b}, ${borderAlpha})`,
    fill: `rgba(${r}, ${g}, ${b}, ${alpha})`,
  };
}
